import SelectDropdown from "components/common/SelectDropdown/SelectDropdown";
import Autocomplete from "@mui/material/Autocomplete";
import {
  MacroAdjustmentCombination,
  MacroAdjustmentCombinations,
  MacroAdjustmentWizardFormikValues,
} from "features/macro-tool/components/MacroAdjustmentWizard/MacroAdjustmentTypes";
import { CountryViewModel, ViewMacroScenarioDto } from "orval/generated/models";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { TextField } from "@mui/material";
import { useMemo } from "react";
import { CheckConflictingEfAdjustment } from "../../HelperFunctions";
import { useGetAllScenariosMacroScenarioIdAdjustmentsGet } from "orval/generated/endpoint";

export type CountriesDropdownStepTwoProps = {
  adjustmentCombination: MacroAdjustmentCombination | undefined;
  scenario: ViewMacroScenarioDto | undefined;
  handleCountriesSave: Function;
};

const CountriesDropdownStepTwo = ({
  adjustmentCombination,
  scenario,
  handleCountriesSave,
}: CountriesDropdownStepTwoProps) => {
  const { t } = useTranslation("macro");

  const { values, setFieldValue } =
    useFormikContext<MacroAdjustmentWizardFormikValues>();

  const { data: adjustments } = useGetAllScenariosMacroScenarioIdAdjustmentsGet(
    scenario?.id as number,
  );

  const countriesList = useMemo(
    () =>
      scenario?.inputs?.countries
        ?.map((country) => country.display_name)
        .sort() || [],
    [scenario?.inputs?.countries],
  );

  const isConflictingEfAdjustment = useMemo<boolean>(() => {
    if (
      adjustments &&
      values.selectedPillar &&
      values.selectedInputArea &&
      values.payload.filters.countries[0]
    ) {
      return CheckConflictingEfAdjustment(
        adjustmentCombination?.combo as MacroAdjustmentCombinations,
        adjustments,
        values.selectedPillar,
        values.selectedInputArea,
        values.payload.filters.countries[0] as string,
        values.id,
      );
    } else {
      return false;
    }
  }, [
    adjustmentCombination?.combo,
    adjustments,
    values.id,
    values.payload.filters.countries,
    values.selectedInputArea,
    values.selectedPillar,
  ]);

  if (!adjustmentCombination) {
    return null;
  }

  switch (adjustmentCombination.combo) {
    case MacroAdjustmentCombinations.Ingredients_IngredientWeights_MultiBrandAdjustment:
    case MacroAdjustmentCombinations.CDE_CDEEnergyMixAndEmissionFactors:
    case MacroAdjustmentCombinations.CDE_CDEFleetBreakdown:
    case MacroAdjustmentCombinations.SalesMix_SalesGrowth:
      return (
        <SelectDropdown
          listItems={countriesList}
          savedSelectedItems={values.selectedCountries?.map(
            (country) => country.display_name,
          )}
          onSave={(selectedItems) => {
            const selectedCountriesAsCountryViewModels = selectedItems?.map(
              (countryDisplayName) => {
                return scenario?.inputs?.countries?.find(
                  (country) => country.display_name === countryDisplayName,
                );
              },
            );

            setFieldValue(
              "selectedCountries",
              selectedCountriesAsCountryViewModels,
            ).catch((error) => {
              console.warn(error);
            });
            handleCountriesSave(
              selectedCountriesAsCountryViewModels as CountryViewModel[],
            );
          }}
          title={`${t("adjustmentsPage.wizard.stepTwo.dropdownTitles.countries")}*`}
          compact
          selectAll={true}
          disabled={scenario?.inputs?.countries?.length === 0}
        />
      );

    default:
      return (
        <Autocomplete
          renderInput={(params) => (
            <TextField
              {...params}
              label={`${t("adjustmentsPage.wizard.stepTwo.dropdownTitles.country")}*`}
              name={"country"}
              {...(isConflictingEfAdjustment
                ? {
                    helperText: (
                      <span
                        style={{
                          color: "rgba(0, 0, 0, 0.5)",
                          fontStyle: "italic",
                        }}
                      >
                        {`${t(
                          "macro:adjustmentsPage.wizard.stepTwo.conflictingCombinationMessagePartOne",
                        )} ${values.selectedInputArea}, ${values.selectedCountries[0].display_name} ${t(
                          "macro:adjustmentsPage.wizard.stepTwo.combination",
                        )}.`}
                      </span>
                    ),
                  }
                : {})}
            />
          )}
          value={values.selectedCountries[0]?.display_name || null}
          options={countriesList}
          size={"small"}
          onChange={(_event, value) => {
            const selectedItemAsCountryViewModel =
              scenario?.inputs?.countries?.find(
                (country) => country.display_name === value,
              );

            if (selectedItemAsCountryViewModel) {
              setFieldValue("selectedCountries", [
                selectedItemAsCountryViewModel,
              ]).catch((error) => {
                console.warn(error);
              });
              handleCountriesSave([
                selectedItemAsCountryViewModel,
              ] as CountryViewModel[]);
            }
          }}
          clearOnBlur
          handleHomeEndKeys
        />
      );
  }
};

export default CountriesDropdownStepTwo;
